import type { PbxDialplanCommon, PbxDialplanExpandedGoto, PbxDialplanType } from './PbxDialplan'

export interface PbxExtensionListResponse {
	/** Index of the first extension item returned. */
	offset: number
	/** Number of extension item to return. Note that the actual number of returned items may be lower. */
	limit: number
	/** Number of extension items that can be returned when offset and limit is not considered. This is to be used for paging through the data. */
	total: number
	/** Array of extension items. */
	queues: PbxExtensionListItem[]
}

export interface PbxExtensionListFullResponse {
	/** Index of the first extension item returned. */
	offset: number
	/** Number of extension item to return. Note that the actual number of returned items may be lower. */
	limit: number
	/** Number of extension items that can be returned when offset and limit is not considered. This is to be used for paging through the data. */
	total: number
	/** Array of extension items */
	queues: PbxExtension[]
}

export interface PbxExtensionListItem {
	/** Unique id of the extension item. */
	_id: string
	/** Name/description of the extension item. */
	name: string
	/** The phone number to dial to reach this extension item */
	number: string
	/** Number to redirect to when execution starts. Used to quickly circumvent the extension item. */
	redirect?: string
	/** Notes about the extension item. */
	notes: string
	/** Id of the employee who uses the extension, if any. */
	employee?: string
	/** Direct number the extension can be called with from the outside, null if none. */
	direct?: string
	/** True if this is the primary extension of the employee. */
	primary: boolean
	/** Given if the employee of this extension is set to be deleted, and this extension will be deleted at the same time. */
	employeeDeleteDate?: Date

	phones?: PbxExtensionListItemPhone[]
}

export interface PbxExtensionListItemPhone {
	/** ID of the phone */
	_id: string

	/** Type of phone */
	type: PbxExtensionPhoneType

	/** Phone model, if available */
	model?: string

	/** SIP phones: MAC address if available, otherwise the notes - Mobile phones: the phone number - EXTERNAL: the phone number. */
	identification: string

	/** True if the phone is connected and not blocked */
	online: boolean
}

export interface PbxExtension extends PbxDialplanCommon {
	/** Always 'EXTENSION' */
	type: PbxDialplanType.EXTENSION

	/** ID of the customer the extension belongs to */
	customer: string

	/** Name of employee who uses the extension, or usage/function if not linked to an employee */
	name: string

	/** The phone number to dial to reach this extension, undefined if it is not dial-able */
	number: string

	/** ID of the employee who uses the extension, if any */
	employee?: string

	/** Direct number the extension can be called with from the outside, undefined if none */
	direct?: string

	/** True if this is the primary extension of the employee */
	primary: boolean

	/** True if this extension must be included in the local phone book */
	phonebook: boolean

	/** True to send calls to the extension even if it is already in a call */
	callWaiting: boolean

	/** If true, inbound calls with a secret number will get rejected or prompt the caller to reveal their number */
	blockSecret: boolean

	/** If true, this extension does not have to enter the master PIN code when performing restricted actions */
	autoMaster: boolean

	/** If true, calls made with a mobile phone will always present the mobile phone's number on outbound calls */
	mobileUseOwnNumber: boolean

	/** Alternate customer to bill this extension to */
	billingCustomer?: string

	/** Given if the employee of this extension is set to be deleted */
	employeeDeleteDate?: Date

	/** Dialplan item to send inbound calls to instead. Will only be invoked once pr. call. */
	inboundGoto?: string | PbxDialplanExpandedGoto

	/** A music on hold category to use instead of a regular ringtone when the extension is called. */
	ringtone?: string

	/** Music on hold category to use in personal queue and when putting a call on hold. */
	musicOnHold?: string

	/** Number of seconds SIP phone(s) should ring before mobile phone(s) are also called. Valid values are: 0 (default), 3, 5, 8, 10, 15, 20, 25 or 30. */
	mobileDelay: 0 | 3 | 5 | 8 | 10 | 15 | 20 | 25 | 30

	/** What to do on inbound call when presence state is OFF_WORK for the employee who owns the extension */
	onPresenceOffWork: PbxExtensionInboundAction

	/** What to do on inbound call when presence state is HOLIDAY for the employee who owns the extension */
	onPresenceHoliday: PbxExtensionInboundAction

	/** What to do on inbound call when presence state is BUSY for the employee who owns the extension */
	onPresenceBusy: PbxExtensionInboundAction

	/** No answer handling */
	noAnswer: PbxExtensionNoAnswer

	/** Busy call handling */
	busy: PbxExtensionBusy

	/** Outbound call handling */
	outbound: PbxExtensionOutbound

	/** List of phones that belong to this extension */
	phones?: PbxExtensionPhone[]
}

export enum PbxExtensionInboundAction {
	/** Accept call */
	AVAILABLE = 'AVAILABLE',
	/** Goto to busy state handling */
	BUSY = 'BUSY',
	/** Only accept calls from local or direct to mobile number */
	BUSY_NOT_MOBILE = 'BUSY_NOT_MOBILE',
	/** Only accept calls from numbers on the whitelist */
	WHITELIST = 'WHITELIST',
	/** Only accept calls from numbers on the whitelist or from local callers */
	WHITELIST_INT = 'WHITELIST_INT'
}

export interface PbxExtensionNoAnswer {
	timeout: number
	extAction: NoAnswerAction
	extNumber?: string
	extGoto?: string | PbxDialplanExpandedGoto
	intAction: NoAnswerAction
	intNumber?: string
	intGoto?: string | PbxDialplanExpandedGoto
	mobileAction: NoAnswerMobileAction
	mobileNumber?: string
	mobileGoto?: string | PbxDialplanExpandedGoto
}

export enum NoAnswerAction {
	/** Keep ringing */
	NOTHING = 'NOTHING',
	/** Send to voicemail */
	VOICEMAIL = 'VOICEMAIL',
	/** Divert to the phone number in noAnswer.<int/ext>Number */
	NUMBER = 'NUMBER',
	/** Jump to the dialplan item specified in noAnswer.<int/ext>Goto */
	GOTO = 'GOTO'
}

export enum NoAnswerMobileAction {
	/** Use settings from noAnswe.extXXX */
	EXTERNAL = 'EXTERNAL',
	/** Keep ringing */
	NOTHING = 'NOTHING',
	/** Send to voicemail */
	VOICEMAIL = 'VOICEMAIL',
	/** Divert to the phone number in noAnswer.mobileNumber */
	NUMBER = 'NUMBER',
	/** Jump to the dialplan item specified in noAnswer.mobileGoto */
	GOTO = 'GOTO'
}

export interface PbxExtensionBusy {
	extAction: PbxExtensionBusyAction
	extNumber?: string
	extGoto?: string | PbxDialplanExpandedGoto
	intAction: PbxExtensionBusyAction
	intNumber?: string
	intGoto?: string | PbxDialplanExpandedGoto
	mobileAction: PbxExtensionBusyMobileAction
	mobileNumber?: string
	mobileGoto?: string | PbxDialplanExpandedGoto
}

export enum PbxExtensionBusyAction {
	/** signal busy state/tone (default). */
	BUSY = 'BUSY',
	/** Send to voicemail */
	VOICEMAIL = 'VOICEMAIL',
	/** Divert to the phone number in busy.<ext/int>Number. */
	NUMBER = 'NUMBER',
	/** Jump to the dialplan item specified in busy.<ext/int>Goto. */
	GOTO = 'GOTO',
	/** Send caller to personal queue. */
	QUEUE = 'QUEUE',
	/** Announce that the extension is busy but that the caller can press 1 to get through (only available for busy.intAction). */
	BARGE = 'BARGE'
}

export enum PbxExtensionBusyMobileAction {
	/** Use settings from external (default). */
	EXTERNAL = 'EXTERNAL',
	/** signal busy state/tone. */
	BUSY = 'BUSY',
	/** Send to voicemail */
	VOICEMAIL = 'VOICEMAIL',
	/** Divert to the phone number in busy.<ext/int>Number. */
	NUMBER = 'NUMBER',
	/** Jump to the dialplan item specified in busy.<ext/int>Goto. */
	GOTO = 'GOTO',
	/** Send caller to personal queue. */
	QUEUE = 'QUEUE'
}

export interface PbxExtensionOutbound {
	/** Call restrictions when dialing out */
	destinations: PbxExtensionOutboundDestinations
	/** Caller ID settings */
	callerId: OutboundCallerId
	/** An optional dialplan item to goto when dialing out. Can be used to limit, redirect etc. the extension when making outbound calls. */
	goto?: string | PbxDialplanExpandedGoto
}

export enum PbxExtensionOutboundDestinations {
	/** Can call any number */
	ANY = 'ANY',
	/** Cannot dial out */
	NONE = 'NONE',
	/** Can only dial local extensions */
	LOCAL = 'LOCAL',
	/** Can only dial local extensions and national destinations */
	NATIONAL = 'NATIONAL'
}

export interface OutboundCallerId {
	/** Number to present when making outbound calls. Choose from default PBX number, direct number, mobile phone number, a number on a trunk marked for sharing or SECRET to not present a number.
	 * Defaults to direct number or default PBX number.
	 */
	onPresenceWork: string
	/** If true, then the user gets the option to change to the number in outbound.callerId.onPresenceOffWork when calling out. A beep sounds and if the user press a key within 2 seconds,
	 *  the callerId is changed.
	 */
	onPresenceWorkAsk: boolean
	/** Number to present when making outbound calls. Choose from default PBX number, direct number, mobile phone number, a number on a trunk marked for sharing or SECRET to not present a number.
	 *  Defaults to direct number or default PBX number.
	 */
	onPresenceOffWork: string
	/** If true, then the user gets the option to change to the number in outbound.callerId.onPresenceWork when calling out. A beep sounds and if the user press a key within 2 seconds,
	 *  the callerId is changed.
	 */
	onPresenceOffWorkAsk: boolean
	/** Number to present on next outbound call. Choose from default PBX number, direct number, mobile phone number, a number on a trunk marked for sharing or SECRET to not present a number.
	 *  Ignored when null/blank.
	 */
	onNextCall?: string
}

export interface PbxExtensionPhone {
	/** ID of the phone */
	_id: string

	/** Type of phone */
	type: PbxExtensionPhoneType

	/** Phone model, if available */
	model?: string

	/** SIP phones: MAC address if available, otherwise the notes - Mobile phones: the phone number - EXTERNAL: the phone number. */
	identification: string

	/** True if the phone is connected and not blocked */
	online: boolean

	/** Current DND state of the phone */
	dnd: boolean

	/** Notes field from the phone */
	notes?: string

	/** ID of product (type SIP only) */
	product?: string

	/** ID of rate plan (type MVNO only) */
	ratePlan?: string

	/** ID of new rate plan (type MVNO only) */
	newRatePlan?: string
}

export enum PbxExtensionPhoneType {
	SIP = 'SIP',
	MVNO = 'MVNO',
	EXTERNAL = 'EXTERNAL'
}

export type PbxExtensionResponse = PbxExtension

export interface PbxExtensionCreateRequest extends Omit<PbxExtension, '_id'> {
	/** Id of the customer the extension belongs to */
	customer: string
}

export type PbxExtensionCreateResponse = PbxExtension

export type PbxExtensionUpdateRequest = Partial<Omit<PbxExtension, '_id'>>

export type PbxExtensionUpdateResponse = PbxExtension

export interface PbxExtensionDeleteResponse {
	/** True if the extension was deleted */
	success: boolean
}

export interface PbxExtensionCallerIdNumbersResponseItem {
	/** Phone number */
	number: string
	/** Name of user or usage */
	name: string
}

export type PbxExtensionCallerIdNumbersResponse = PbxExtensionCallerIdNumbersResponseItem[]

export type PbxExtensionDirectNumbersResponse = string[]

export interface PbxExtensionDndRequest {
	/** Id of SIP phone to set DND state for. */
	sip?: string
	/** Id of MVNO phone to set DND state for. */
	mvno?: string
	/** Id of EXTERNAL phone to set DND state for. */
	external?: string
	/** True to enable, false to disable, omit to toggle. */
	state?: boolean
}

export interface PbxExtensionDndResponse {
	/** True if enabled, false if disabled */
	dnd: boolean
}

export interface PbxExtensionRedirectRequest {
	/** True to enable redirection, false to disable. */
	enable: boolean
	/** Phone number to redirect to, local extension number or public E.164 formatted number. Only used if enabling. */
	number?: string
}

export interface PbxExtensionRedirectResponse {
	/** True if redirect has been enabled, false if it has been disabled. */
	state: boolean
	/** Phone number to redirect to, E.164 format. Not used if disabled. */
	number?: string
}
